import DOMPurify from "dompurify";

export const TEXT_COLOR_CLASSES = {
  "#8c8c8c": "av-sanitized-color-gray",
  "#ff4d4f": "av-sanitized-color-red",
  "#faad14": "av-sanitized-color-gold",
  "#52c41a": "av-sanitized-color-green",
  "#1677ff": "av-sanitized-color-blue",
  "#722ed1": "av-sanitized-color-purple",
  "rgb(140, 140, 140)": "av-sanitized-color-gray",
  "rgb(255, 77, 79)": "av-sanitized-color-red",
  "rgb(250, 173, 20)": "av-sanitized-color-gold",
  "rgb(82, 196, 26)": "av-sanitized-color-green",
  "rgb(22, 119, 255)": "av-sanitized-color-blue",
  "rgb(114, 46, 209)": "av-sanitized-color-purple",
};

export const FONT_SIZE_LADDER_PX = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 72] as const;

export function generateFontSizeCssRules(prefix = ""): string {
  const selectorPrefix = prefix ? `${prefix} ` : "";

  return FONT_SIZE_LADDER_PX.map(
    (size) => `${selectorPrefix}.av-sanitized-font-size-${size} { font-size: ${size}px; }`
  ).join("\n");
}

export const FONT_SIZE_CLASSES = Object.fromEntries(
  FONT_SIZE_LADDER_PX.map((size) => [`${size}px`, `av-sanitized-font-size-${size}`])
) as Record<string, string>;

const EM_BASE_PX = 16;
const PT_TO_PX = 96 / 72;

function parseFontSizeToPx(value: string): number | null {
  const match = value
    .trim()
    .toLowerCase()
    .match(/^(\d*\.?\d+)(px|pt|em|rem)$/);

  if (!match) {
    return null;
  }

  const amount = Number(match[1]);

  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }

  switch (match[2]) {
    case "px":
      return amount;
    case "pt":
      return amount * PT_TO_PX;
    case "em":
    case "rem":
      return amount * EM_BASE_PX;
    default:
      return null;
  }
}

function snapToFontSizeLadder(px: number): (typeof FONT_SIZE_LADDER_PX)[number] {
  return FONT_SIZE_LADDER_PX.reduce((nearest, candidate) =>
    Math.abs(candidate - px) < Math.abs(nearest - px) ? candidate : nearest
  );
}

export function fontSizeClassForDeclaration(value: string): string | null {
  const px = parseFontSizeToPx(value);

  if (px === null) {
    return null;
  }

  return `av-sanitized-font-size-${snapToFontSizeLadder(px)}`;
}

const ALLOWED_SPAN_CLASSES = new Set<string>([
  ...Object.values(TEXT_COLOR_CLASSES),
  ...Object.values(FONT_SIZE_CLASSES),
]);

const COLOR_CLASS_TO_VALUE = new Map<string, string>();

for (const [value, className] of Object.entries(TEXT_COLOR_CLASSES)) {
  if (!COLOR_CLASS_TO_VALUE.has(className)) {
    COLOR_CLASS_TO_VALUE.set(className, value);
  }
}

const FONT_SIZE_CLASS_TO_VALUE = new Map<string, string>(
  Object.entries(FONT_SIZE_CLASSES).map(([value, className]) => [className, value])
);

const TRANSPORT_ALLOWED_TAGS = [
  "p",
  "br",
  "span",
  "strong",
  "b",
  "em",
  "i",
  "s",
  "strike",
  "u",
  "ul",
  "ol",
  "li",
  "blockquote",
  "code",
  "pre",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "a",
];

const TRANSPORT_ALLOWED_ATTR = ["style", "class", "href", "target", "rel"];

export function normalizeColorValue(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s*,\s*/g, ", ")
    .replace(/\s+/g, " ");
}

export function convertSpanStyleToClasses(element: HTMLElement) {
  const declarations = (element.getAttribute("style") ?? "").split(";");
  const nextClasses: string[] = [];
  let hasInvalidDeclaration = false;

  for (const declaration of declarations) {
    if (!declaration.trim()) continue;

    const separatorIndex = declaration.indexOf(":");

    if (separatorIndex === -1) {
      hasInvalidDeclaration = true;
      break;
    }

    const property = declaration.slice(0, separatorIndex).trim().toLowerCase();
    const value = declaration.slice(separatorIndex + 1).trim();

    if (property === "color") {
      const className =
        TEXT_COLOR_CLASSES[normalizeColorValue(value) as keyof typeof TEXT_COLOR_CLASSES];

      if (!className) {
        hasInvalidDeclaration = true;
        break;
      }

      nextClasses.push(className);
      continue;
    }

    if (property === "font-size") {
      const className = fontSizeClassForDeclaration(value);

      if (!className) {
        hasInvalidDeclaration = true;
        break;
      }

      nextClasses.push(className);
      continue;
    }

    hasInvalidDeclaration = true;
    break;
  }

  element.removeAttribute("style");

  if (hasInvalidDeclaration || nextClasses.length === 0) {
    return;
  }

  element.classList.add(...nextClasses);
}

export function convertSpanClassesToStyle(element: HTMLElement) {
  const declarations: string[] = [];

  for (const token of Array.from(element.classList)) {
    const color = COLOR_CLASS_TO_VALUE.get(token);

    if (color) {
      declarations.push(`color: ${color}`);
      element.classList.remove(token);
      continue;
    }

    const fontSize = FONT_SIZE_CLASS_TO_VALUE.get(token);

    if (fontSize) {
      declarations.push(`font-size: ${fontSize}`);
      element.classList.remove(token);
    }
  }

  if (element.classList.length === 0) {
    element.removeAttribute("class");
  }

  if (declarations.length === 0) {
    return;
  }

  const existing = (element.getAttribute("style") ?? "").trim().replace(/;\s*$/, "");
  const merged = [existing, ...declarations].filter(Boolean).join("; ");

  element.setAttribute("style", `${merged};`);
}

export function rehydrateSanitizedHtmlForEditor(htmlString: string) {
  if (typeof document === "undefined") {
    return htmlString;
  }

  // Materialize a style attribute so TipTap's textStyle mark accepts the span
  // (its parse rule only matches spans that carry `style`). The attribute value
  // survives CSP even though CSP blocks CSSOM access to it, so the editor reads
  // it back via getAttribute rather than element.style.
  const doc = new DOMParser().parseFromString(htmlString, "text/html");

  doc.querySelectorAll<HTMLElement>("span[class]").forEach((element) => {
    convertSpanClassesToStyle(element);
  });

  return doc.body.innerHTML;
}

function stripUnknownClasses(element: HTMLElement) {
  const allowed = Array.from(element.classList).filter((token) => ALLOWED_SPAN_CLASSES.has(token));

  if (allowed.length === 0) {
    element.removeAttribute("class");

    return;
  }

  element.setAttribute("class", allowed.join(" "));
}

export function normalizeEditorHtmlForTransport(htmlString: string) {
  const sanitized = DOMPurify.sanitize(htmlString, {
    ALLOWED_TAGS: TRANSPORT_ALLOWED_TAGS,
    ALLOWED_ATTR: TRANSPORT_ALLOWED_ATTR,
  });

  if (typeof document === "undefined") {
    return sanitized;
  }

  // This function only reads and removes style attributes — it never writes them,
  // so CSP does not block it. DOMParser is not required here.
  const template = document.createElement("template");

  template.innerHTML = sanitized;

  template.content.querySelectorAll<HTMLElement>("[style]").forEach((element) => {
    if (element.tagName !== "SPAN") {
      element.removeAttribute("style");

      return;
    }

    convertSpanStyleToClasses(element);
  });

  template.content.querySelectorAll<HTMLElement>("[class]").forEach((element) => {
    if (element.tagName !== "SPAN") {
      element.removeAttribute("class");

      return;
    }

    stripUnknownClasses(element);
  });

  return template.innerHTML;
}
